import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { connect } from 'react-redux'
import OspreyApi from '../../actions/osprey-api'

const pathSeparator = '.'

export function parseMethodName(name, pathSeparator, aliasName) {
    const [resource, method] = name.split(pathSeparator)

    if (!resource || !method) {
        throw new Error(`Invalid method name: '${name}'`)
    }

    return {
        resource,
        method,
        alias: aliasName || method
    }
}

export function parseMethodNames(methods, pathSeparator) {
    if (Array.isArray(methods)) {
        return methods.map(name => parseMethodName(name, pathSeparator))
    }
    switch (typeof methods) {
        case 'object':
            return Object.keys(methods).map(key => {
                const value = methods[key]
                switch (typeof value) {
                    case 'string': {
                        return parseMethodName(value, pathSeparator, key)
                    }

                    case 'object': {
                        const { resource, method, onError } = value
                        if (!resource) {
                            throw new Error(`Missing 'resource' key in object '${key}'`)
                        }
                        if (!method) {
                            throw new Error(`Missing 'method' key in object '${key}'`)
                        }
                        if (onError && typeof onError !== 'function') {
                            throw new Error(`onError must be a function for resource '${resource}' method '${method}'`)
                        }

                        return Object.assign({ alias: key }, value)
                    }

                    default: {
                        throw new Error('Invalid method data type')
                    }
                }
            })

        case 'string':
            return [parseMethodName(methods, pathSeparator)]

        default:
            throw new Error('Invalid method data type.')
    }
}

export default function withOspreyApiConnector(ChildComponent, options) {

    const methods = parseMethodNames(options.methods, pathSeparator)

    class WithOspreyApiWrapper extends Component {
        static propTypes = {
            contentByKey: PropTypes.object.isRequired,
            dispatch: PropTypes.func.isRequired,
            ospreyApi: PropTypes.object.isRequired,
        }

        constructor(props) {
            super(props)

            const { dispatch, ospreyApi } = props

            const actions = {}
            const dispatchToPropsStore = this.mapDispatchToProps(dispatch, ospreyApi, methods, actions)

            this.state = {
                actions,
                actionResultToPropsStore: {},
                dispatchToPropsStore
            }
        }

        componentWillReceiveProps(nextProps) {
            const { contentByKey } = nextProps
            const { actions } = this.state

            const actionResultToPropsStore = this.mapActionResultsToProps(methods, actions, contentByKey)

            this.setState({
                actionResultToPropsStore
            })
        }

        mapDispatchToProps(dispatch, api, methods, actions) {
            return methods.reduce((props, nameInfo) => {
                const { resource, method, alias, dataKey, onError } = nameInfo

                if (!api[resource]) {
                    throw new Error(`Invalid resource name: '${resource}'`)
                }

                if (!api[resource][method]) {
                    throw new Error(`Invalid method name: '${resource}.${method}'`)
                }

                const action = api[resource][method](dataKey, onError)

                actions[alias] = action
                props[alias] = (...params) => {
                    return dispatch(action[method](...params))
                }

                return props
            }, {})
        }

        mapActionResultsToProps(methods, actions, contentByKey) {
            return methods.reduce((props, nameInfo) => {
                const { alias, resultName, getResultState } = nameInfo

                const contentKey = resultName || `${alias}Result`
                const methodState = actions[alias].getState(contentByKey)

                props[contentKey] = getResultState
                    ? getResultState(methodState)
                    : this.getMethodStateValue(methodState)

                return props
            }, {})
        }

        getMethodStateValue(methodState) {
            const {
                content,
                isFetching,
                success
            } = methodState

            if (!success || isFetching) {
                return undefined
            }

            return content
        }

        render() {
            const { contentByKey, dispatch, ospreyApi, ...others } = this.props // eslint-disable-line no-unused-vars
            const { dispatchToPropsStore, actionResultToPropsStore } = this.state

            const childProps = {
                ...dispatchToPropsStore,
                ...actionResultToPropsStore,
                ...others
            }

            return <ChildComponent {...childProps} />
        }
    }

    const mapStateToProps = (state) => {
        const { contentByKey } = state
        const ospreyApi = new OspreyApi()

        return {
            contentByKey,
            ospreyApi
        }
    }

    return connect(mapStateToProps)(WithOspreyApiWrapper)
}



// WEBPACK FOOTER //
// src/containers/high-order-components/with-osprey-api-connector.js